import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { isAxiosError } from 'axios'
import React from 'react'
import { ERROR_CODES, ERROR_MESSAGES } from 'constants/common'
import type { ToastProps } from 'hooks/useToast'
import { useToast } from 'hooks/useToast'

const getQueryClient = (toast: ({ message, status, id }: ToastProps) => void) =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error: Error) => {
        if (!isAxiosError(error)) {
          console.error(error)

          toast({
            id: 'generic-error',
            status: 'danger',
            message: 'There was unexpected error.',
            longMessage: error.message,
          })
          return
        }

        const { response, message, config } = error

        if (config?.url?.includes('/accounts/')) {
          // TODO[zircuit-api-problem]: remove this once we have a proper error handling for accounts
          return
        }

        if (response?.status === ERROR_CODES.tooManyRequests) {
          toast({
            id: ERROR_MESSAGES.tooManyRequests,
            status: 'danger',
            message: ERROR_MESSAGES.tooManyRequests,
            longMessage: message,
          })
        } else {
          toast({
            id: message,
            status: 'danger',
            message: 'Error fetching data.',
            longMessage: message,
          })
        }
      },
    }),
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  })

export const ReactQueryClientProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const toast = useToast()

  return (
    <QueryClientProvider client={getQueryClient(toast)}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}
